import { useState } from 'react';
import { AccountStatus } from '@hungryroot/customer';
import { fullPageRedirect } from '@hungryroot/environment';
import { useUnskipOrder } from '@hungryroot/orders';
import { Modal as StyledSheetOrModal } from '@hungryroot/ramen';
import { useReactivateAccount, useUnholdAccount } from '@hungryroot/rest';
import { REACTIVATE_ROUTE } from '@hungryroot/routes';

interface AddToCartModalProps {
  testid?: string;
  accountStatus?: AccountStatus;
  orderId?: number;
  onAccountChangeSuccess: () => void;
  onCancel: () => void;
  initialShowModal: boolean;
  callout?: string;
  showXButton?: boolean;
  unskipAndEdit?: boolean;
}

function getContent(
  accountStatus: AccountStatus | undefined,
  unskipAndEdit?: boolean
) {
  switch (accountStatus) {
    case AccountStatus.Canceled:
      return {
        heading: 'Resume your deliveries?',
        body: "You've canceled your deliveries. When you resubscribe, you can shop and add any items to your cart.",
        ctaText: 'Resume and add item',
      };
    case AccountStatus.Skipped:
      return {
        heading: 'Unskip your delivery?',
        body: `You've skipped this delivery. Would you like to ${
          unskipAndEdit
            ? 'unskip and edit recipe'
            : 'unskip and update your cart'
        }?`,
        ctaText: unskipAndEdit
          ? 'Unskip and edit recipe'
          : 'Unskip and update cart',
      };
    case AccountStatus.Paused:
      return {
        heading: 'Unpause your deliveries?',
        body: `You've paused your deliveries. Would you like to  ${
          unskipAndEdit
            ? 'resume and edit recipe'
            : 'resume and update your cart'
        }?`,
        ctaText: unskipAndEdit
          ? 'Unpause and add item'
          : 'Unpause and edit recipe',
      };
    default:
      return;
  }
}

export function AddToCartModal(props: AddToCartModalProps) {
  const {
    testid,
    initialShowModal,
    onAccountChangeSuccess,
    onCancel,
    accountStatus,
    orderId,
    showXButton,
    unskipAndEdit = false,
  } = props;
  const [modalIsOpen, setModalIsOpen] = useState(initialShowModal);

  const useReactivateAccountMutation = useReactivateAccount({
    onSuccess() {
      fullPageRedirect(REACTIVATE_ROUTE);
    },
  });
  const useUnskipOrderMutation = useUnskipOrder();
  const useUnpauseAccountMutation = useUnholdAccount();

  function onAcceptOffer(
    accountStatus: AccountStatus | undefined,
    onMutationSuccess: () => void
  ) {
    switch (accountStatus) {
      case AccountStatus.Canceled:
        useReactivateAccountMutation.mutate(undefined, {
          onSuccess: () => {
            onMutationSuccess();
            setModalIsOpen(false);
          },
        });
        break;
      case AccountStatus.Skipped:
        if (!orderId) {
          return;
        }
        useUnskipOrderMutation.mutate(
          {
            data: { id: orderId, source: 'add-to-cart-modal' },
          },
          {
            onSuccess: () => {
              onMutationSuccess();
              setModalIsOpen(false);
            },
          }
        );
        break;
      case AccountStatus.Paused:
        useUnpauseAccountMutation.mutate(undefined, {
          onSuccess: () => {
            onMutationSuccess();
            setModalIsOpen(false);
          },
        });
        break;
    }
  }

  const content = getContent(accountStatus, unskipAndEdit);

  if (content === undefined) {
    return null;
  }

  const { heading, body, ctaText } = content;

  return (
    <StyledSheetOrModal
      confirmButtonText={ctaText}
      secondaryButtonText="Cancel"
      data-testid={testid}
      onDismiss={() => {
        onCancel();
        setModalIsOpen(false);
      }}
      showXButton={showXButton}
      ariaLabel="AddToCartModal"
      testid={testid}
      isOpen={modalIsOpen}
      heading={heading}
      body={body}
      onActionClick={() => {
        onAcceptOffer(accountStatus, onAccountChangeSuccess);
      }}
    />
  );
}
